import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTenantSettings } from "@/hooks/useTenantSettings";
import { useKioskCommissionSlabs } from "@/hooks/useKioskCommissionSlabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, ArrowDownLeft, Calculator, Banknote, Wifi } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const transactionTypes = [
  { value: "withdraw", label: "Withdraw", icon: ArrowUpRight },
  { value: "deposit", label: "Deposit", icon: ArrowDownLeft },
  { value: "balance_enquiry", label: "Balance Enquiry", icon: Calculator },
  { value: "mini_statement", label: "Mini Statement", icon: Calculator },
];

interface AddKioskTransactionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddKioskTransactionModal({ 
  open, 
  onOpenChange,
  onSuccess 
}: AddKioskTransactionModalProps) {
  const { user, tenant } = useAuth();
  const { settings } = useTenantSettings();
  const { calculatePortalCommission, isLoading: slabsLoading } = useKioskCommissionSlabs();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [formData, setFormData] = useState({
    transaction_type: "withdraw",
    payment_method: "aeps", // AEPS or UPI for withdraw; DMT or UPI for deposit
    aadhaar_last_four: "",
    customer_mobile: "",
    customer_name: "",
    amount: "",
    bank_name: "",
    manual_commission: "",
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        transaction_type: "withdraw",
        payment_method: "aeps",
        aadhaar_last_four: "",
        customer_mobile: "",
        customer_name: "",
        amount: "",
        bank_name: "",
        manual_commission: "",
      });
    }
  }, [open]);

  const createTransaction = useMutation({
    mutationFn: async (data: any) => {
      const { error } = await supabase.from("kiosk_transactions").insert(data);
      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all kiosk-related queries
      queryClient.invalidateQueries({ queryKey: ["kiosk_transactions"] });
      queryClient.invalidateQueries({ queryKey: ["kiosk-transactions-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["kiosk-transactions-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["kiosk-stats"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      // Invalidate daily book queries to update cash counter
      queryClient.invalidateQueries({ queryKey: ["daily-book"] });
      queryClient.invalidateQueries({ queryKey: ["daily-kiosk"] });
      toast({ title: "Transaction recorded successfully" });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Calculate portal commission based on slabs (for withdraw/deposit only)
  // Portal commission only applies to AEPS (withdraw) - NOT to UPI or DMT
  const amount = parseFloat(formData.amount) || 0;
  const shouldAddPortalCommission = 
    (formData.transaction_type === "withdraw" && formData.payment_method === "aeps");
  const portalCommission = shouldAddPortalCommission 
    ? calculatePortalCommission(formData.transaction_type, amount) 
    : 0;
  const manualCommission = parseFloat(formData.manual_commission) || 0;

  // For balance enquiry and mini statement, use fixed rates from settings
  const getFixedCommission = () => {
    if (formData.transaction_type === "balance_enquiry") {
      return settings?.kiosk_balance_commission || 3;
    }
    if (formData.transaction_type === "mini_statement") {
      return settings?.kiosk_mini_statement_commission || 5;
    }
    return 0;
  };

  const isWithdrawOrDeposit = formData.transaction_type === "withdraw" || formData.transaction_type === "deposit";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !tenant?.id) return;

    // Calculate total commission for legacy compatibility
    const totalCommission = isWithdrawOrDeposit 
      ? manualCommission + portalCommission 
      : manualCommission + getFixedCommission();

    createTransaction.mutate({
      tenant_id: tenant.id,
      user_id: user.id,
      transaction_type: formData.transaction_type,
      aadhaar_last_four: formData.aadhaar_last_four || null,
      customer_mobile: formData.customer_mobile || null,
      customer_name: formData.customer_name || null,
      amount: amount || 0,
      bank_name: formData.bank_name || null,
      manual_commission: manualCommission,
      portal_commission: isWithdrawOrDeposit ? portalCommission : getFixedCommission(),
      commission_amount: totalCommission, // Legacy field for backward compatibility
      status: "success",
    });
  };

  const TypeIcon = transactionTypes.find(t => t.value === formData.transaction_type)?.icon || Calculator;

  // Form content - shared between Dialog and Drawer
  const FormContent = (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 px-1">
      {/* Transaction Type - Large touch-friendly buttons */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Transaction Type</Label>
        <div className="grid grid-cols-2 gap-2">
          {transactionTypes.map((type) => (
            <Button
              key={type.value}
              type="button"
              variant={formData.transaction_type === type.value ? "default" : "outline"}
              className="h-12 flex items-center justify-center gap-2"
              onClick={() => setFormData({ 
                ...formData, 
                transaction_type: type.value, 
                payment_method: type.value === "withdraw" ? "aeps" : type.value === "deposit" ? "dmt" : "",
                amount: "", 
                manual_commission: "" 
              })}
            >
              <type.icon className="h-4 w-4" />
              <span className="text-sm">{type.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Payment Method Selection - Only for Withdraw/Deposit */}
      {isWithdrawOrDeposit && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {formData.transaction_type === "withdraw" ? "Withdrawal Method" : "Deposit Method"}
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {formData.transaction_type === "withdraw" ? (
              <>
                <Button
                  type="button"
                  variant={formData.payment_method === "aeps" ? "default" : "outline"}
                  className="h-10"
                  onClick={() => setFormData({ ...formData, payment_method: "aeps" })}
                >
                  AEPS
                </Button>
                <Button
                  type="button"
                  variant={formData.payment_method === "upi" ? "default" : "outline"}
                  className="h-10"
                  onClick={() => setFormData({ ...formData, payment_method: "upi" })}
                >
                  UPI
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant={formData.payment_method === "dmt" ? "default" : "outline"}
                  className="h-10"
                  onClick={() => setFormData({ ...formData, payment_method: "dmt" })}
                >
                  DMT
                </Button>
                <Button
                  type="button"
                  variant={formData.payment_method === "upi" ? "default" : "outline"}
                  className="h-10"
                  onClick={() => setFormData({ ...formData, payment_method: "upi" })}
                >
                  UPI
                </Button>
              </>
            )}
          </div>
          {formData.transaction_type === "withdraw" && formData.payment_method === "aeps" && (
            <p className="text-xs text-blue-600">Portal commission will be added for AEPS</p>
          )}
          {(formData.payment_method === "upi" || formData.payment_method === "dmt") && (
            <p className="text-xs text-muted-foreground">No portal commission for {formData.payment_method.toUpperCase()}</p>
          )}
        </div>
      )}

      {/* Aadhaar & Mobile */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-sm">Aadhaar Last 4</Label>
          <Input
            value={formData.aadhaar_last_four}
            onChange={(e) => setFormData({ ...formData, aadhaar_last_four: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            maxLength={4}
            placeholder="1234"
            inputMode="numeric"
            className="h-12 text-base"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Mobile</Label>
          <Input
            value={formData.customer_mobile}
            onChange={(e) => setFormData({ ...formData, customer_mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
            maxLength={10}
            placeholder="9876543210"
            inputMode="tel"
            className="h-12 text-base"
          />
        </div>
      </div>

      {/* Customer Name */}
      <div className="space-y-1.5">
        <Label className="text-sm">Customer Name (Optional)</Label>
        <Input
          value={formData.customer_name}
          onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
          placeholder="Enter name"
          className="h-12 text-base"
        />
      </div>

      {/* Amount & Bank - Only for Withdraw/Deposit */}
      {isWithdrawOrDeposit && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">Amount (₹)</Label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0;
                if (val <= 500000) {
                  setFormData({ ...formData, amount: e.target.value });
                }
              }}
              placeholder="0"
              max={500000}
              inputMode="numeric"
              className="h-12 text-base font-medium"
            />
            <p className="text-xs text-muted-foreground">Max ₹5,00,000</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Bank Name</Label>
            <Input
              value={formData.bank_name}
              onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
              placeholder="SBI, HDFC..."
              className="h-12 text-base"
            />
          </div>
        </div>
      )}

      {/* Cash Commission */}
      <div className="space-y-1.5">
        <Label className="flex items-center gap-2 text-sm">
          <Banknote className="h-4 w-4 text-green-600" />
          Cash Commission
        </Label>
        <Input
          type="number"
          value={formData.manual_commission}
          onChange={(e) => setFormData({ ...formData, manual_commission: e.target.value })}
          placeholder="0"
          min={0}
          inputMode="numeric"
          className="h-12 text-base font-medium"
        />
        <p className="text-xs text-muted-foreground">Commission collected from customer</p>
      </div>

      {/* Commission Summary - Compact */}
      <div className="rounded-lg border border-border bg-muted/50 p-3 space-y-2">
        <p className="text-sm font-semibold">Commission Summary</p>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Banknote className="h-4 w-4 text-green-600" />
            <div>
              <p className="text-xs text-muted-foreground">Cash</p>
              <p className="text-base font-bold text-green-600">₹{manualCommission.toFixed(0)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Wifi className="h-4 w-4 text-blue-600" />
            <div>
              <p className="text-xs text-muted-foreground">Portal</p>
              <p className="text-base font-bold text-blue-600">
                ₹{(isWithdrawOrDeposit ? portalCommission : getFixedCommission()).toFixed(0)}
              </p>
            </div>
          </div>
          <div className="border-l border-border pl-4">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-lg font-bold text-primary">
              ₹{(manualCommission + (isWithdrawOrDeposit ? portalCommission : getFixedCommission())).toFixed(0)}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => onOpenChange(false)} 
          className="flex-1 h-12 text-base"
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={createTransaction.isPending || slabsLoading} 
          className="flex-1 h-12 text-base font-semibold"
        >
          {createTransaction.isPending ? "Saving..." : "Add Transaction"}
        </Button>
      </div>
    </form>
  );

  // Mobile: Use Drawer (slides from bottom)
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="flex items-center gap-2 text-lg">
              <TypeIcon className="h-5 w-5" />
              Add Kiosk Transaction
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-6">
            {FormContent}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Use Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TypeIcon className="h-5 w-5" />
            Add Kiosk Transaction
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 pr-2">
          {FormContent}
        </div>
      </DialogContent>
    </Dialog>
  );
}